import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';

import { importLayerProduction } from '@/api/layers';
import {
  detectCsvDelimiter,
  getCsvColumnOptions,
  parseCsvContent,
  type CsvDelimiterMode,
} from '@/utils/csvImport';
import {
  buildLayerProductionImportRows,
  parseCsvTable,
  type LayerProductionImportRow,
} from '@/utils/layerCsvImportValidation';

interface LayerProductionImportCsvModalProps {
  open: boolean;
  onClose: () => void;
  layerId: string;
  hasExistingData: boolean;
  onImported: () => void;
}

const COLUMN_FIELDS = [
  { key: 'yearColumn', label: 'Year', required: true },
  { key: 'oilColumn', label: 'Oil (1000 t)' },
  { key: 'gasColumn', label: 'Gas (1000 t)' },
  { key: 'waterColumn', label: 'Water (1000 t)' },
] as const;

type ColumnMapping = Record<(typeof COLUMN_FIELDS)[number]['key'], string>;

const EMPTY_MAPPING: ColumnMapping = {
  yearColumn: '',
  oilColumn: '',
  gasColumn: '',
  waterColumn: '',
};

export const LayerProductionImportCsvModal: React.FC<LayerProductionImportCsvModalProps> = ({
  open,
  onClose,
  layerId,
  hasExistingData,
  onImported,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState('');
  const [rawCsv, setRawCsv] = useState('');
  const [delimiterMode, setDelimiterMode] = useState<CsvDelimiterMode>('auto');
  const [mapping, setMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedRows, setParsedRows] = useState<LayerProductionImportRow[]>([]);

  const table = useMemo(() => {
    if (!rawCsv) return null;
    return parseCsvTable(rawCsv, delimiterMode);
  }, [rawCsv, delimiterMode]);

  const columnOptions = useMemo(
    () => (table ? getCsvColumnOptions(table.headers) : []),
    [table],
  );

  const reset = () => {
    setFileName('');
    setRawCsv('');
    setDelimiterMode('auto');
    setMapping(EMPTY_MAPPING);
    setError(null);
    setConfirmOpen(false);
    setImporting(false);
    setParsedRows([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
    if (!isCsv) {
      setError('Only CSV files are supported.');
      return;
    }

    try {
      const text = await file.text();
      const rows = parseCsvContent(text, 'auto');
      if (rows.length < 2) {
        setError('CSV must contain a header row and at least one data row.');
        return;
      }
      setFileName(file.name);
      setRawCsv(text);
      setDelimiterMode('auto');
      setMapping(EMPTY_MAPPING);
      setError(null);
      setParsedRows([]);
    } catch {
      setError('Failed to read CSV file.');
    }
  };

  const handleValidate = () => {
    if (!table) {
      setError('Upload a CSV file first.');
      return;
    }
    const result = buildLayerProductionImportRows(table, mapping);
    if (result.error) {
      setError(result.error);
      setParsedRows([]);
      return;
    }
    setError(null);
    setParsedRows(result.rows);
    setConfirmOpen(true);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      await importLayerProduction(layerId, parsedRows);
      onImported();
      handleClose();
    } catch (err) {
      console.error('Failed to import layer production:', err);
      setError('Failed to import production data.');
      setConfirmOpen(false);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Import reservoir production from CSV</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Oil, gas and water: <strong>thousand metric tons (1000 t)</strong>. Gas is stored as oil-equivalent mass.
          </Alert>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
              Upload CSV
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName || 'No file selected'}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleFileChange}
            />
          </Box>

          {rawCsv && (
            <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
              <InputLabel>Delimiter</InputLabel>
              <Select
                label="Delimiter"
                value={delimiterMode}
                onChange={(e) => setDelimiterMode(e.target.value as CsvDelimiterMode)}
              >
                <MenuItem value="auto">Auto ({detectCsvDelimiter(rawCsv) === '\t' ? 'tab' : detectCsvDelimiter(rawCsv)})</MenuItem>
                <MenuItem value=";">Semicolon (;)</MenuItem>
                <MenuItem value=",">Comma (,)</MenuItem>
                <MenuItem value={'\t'}>Tab</MenuItem>
                <MenuItem value="|">Pipe (|)</MenuItem>
              </Select>
            </FormControl>
          )}

          {table && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Map columns
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 2 }}>
                {COLUMN_FIELDS.map((field) => (
                  <FormControl key={field.key} size="small" fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={mapping[field.key]}
                      onChange={(e) => setMapping((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    >
                      {columnOptions.map((option) => (
                        <MenuItem key={`${field.key}-${option.value}`} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Year must be a calendar year (e.g. 2020). Years must be consecutive without duplicates or gaps.
                At least oil or gas column is required. Oil, gas and water in 1000 t.
              </Typography>
              <TableContainer sx={{ maxHeight: 200, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {table.headers.map((header, index) => (
                        <TableCell key={index}>{header || `Column ${index + 1}`}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {table.rows.slice(0, 5).map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        {table.headers.map((_, colIndex) => (
                          <TableCell key={colIndex}>{row[colIndex] ?? ''}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleValidate} disabled={!table}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => !importing && setConfirmOpen(false)}>
        <DialogTitle>Confirm import</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {hasExistingData
              ? 'This reservoir already has production data. It will be permanently replaced with data from the CSV file.'
              : `Import ${parsedRows.length} production rows for this reservoir?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={importing}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleImport} disabled={importing}>
            {importing ? <CircularProgress size={20} /> : 'Replace and import'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
